import React, { Fragment, useState, useEffect } from 'react';
import {
  HardHat,
  Plus,
  Edit2,
  AlertTriangle,
  Shield,
  Package,
  BarChart3,
  Loader2,
  X,
  TrendingUp,
  Users,
  Search,
  Trash2,
  Eye,
  CheckCircle2,
  Table2,
  LineChart as LineChartIcon,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { sstApi, usersApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';

const TYPE_LABELS = {
  accident: { label: 'Acidente', color: 'bg-red-500/20 text-red-400' },
  incident: { label: 'Incidente', color: 'bg-orange-500/20 text-orange-400' },
  near_miss: { label: 'Quase Acidente', color: 'bg-yellow-500/20 text-yellow-400' },
  occupational_disease: { label: 'Doença Ocupacional', color: 'bg-purple-500/20 text-purple-400' },
};

const SEVERITY_LABELS = {
  minor: { label: 'Menor', color: 'bg-green-500/20 text-green-400' },
  moderate: { label: 'Moderada', color: 'bg-yellow-500/20 text-yellow-400' },
  serious: { label: 'Grave', color: 'bg-orange-500/20 text-orange-400' },
  fatal: { label: 'Fatal', color: 'bg-red-500/20 text-red-400' },
};

const STATUS_LABELS = {
  open: { label: 'Aberta', color: 'bg-blue-500/20 text-blue-400' },
  investigating: { label: 'Em Investigação', color: 'bg-yellow-500/20 text-yellow-400' },
  action_pending: { label: 'Ação Pendente', color: 'bg-orange-500/20 text-orange-400' },
  closed: { label: 'Encerrada', color: 'bg-green-500/20 text-green-400' },
};

// F-029 - Relatório de Ocorrência - SSMA
const EVENT_CATEGORY_LABELS = {
  injury_illness: 'Lesão ou Doença',
  property_damage: 'Dano a Propriedade',
  environmental_damage: 'Dano Ambiental',
  incident: 'Incidente',
};

const INJURY_ILLNESS_SUBTYPE_LABELS = {
  incapacitating: 'Incapacitante',
  non_incapacitating: 'Não-incapacitante',
  fatal: 'Fatal',
  occupational_disease: 'Enfermidade ocupacional',
};

const PROPERTY_DAMAGE_SUBTYPE_LABELS = {
  fire_explosion: 'Incêndios e/ou Explosões',
  infrastructure: 'Infraestrutura',
  process_loss: 'Perda de Processo',
  property_loss: 'Perda de Propriedade',
};

const ENVIRONMENTAL_DAMAGE_SUBTYPE_LABELS = {
  air: 'Ar',
  water: 'Água',
  soil: 'Solo',
  other: 'Outros',
};

const INCIDENT_SUBTYPE_LABELS = {
  personal: 'Pessoal',
  property: 'Propriedade',
  environmental: 'Ambiental',
  other: 'Outros',
};

const SEVERITY_POTENTIAL_LABELS = {
  low: { label: 'Baixo', color: 'bg-green-500/20 text-green-400' },
  medium: { label: 'Médio', color: 'bg-yellow-500/20 text-yellow-400' },
  high: { label: 'Alto', color: 'bg-orange-500/20 text-orange-400' },
  extreme: { label: 'Altíssimo', color: 'bg-red-500/20 text-red-400' },
};

const EMPTY_INJURED_PERSON = { name: '', registration: '', age: '', function: '', time_in_function: '' };

// Optional enum fields on the occurrence form: the backend expects null when
// unselected, not an empty string, so these must be nulled out before sending.
const OCCURRENCE_NULLABLE_ENUM_FIELDS = [
  'event_category', 'injury_illness_subtype', 'property_damage_subtype',
  'environmental_damage_subtype', 'incident_subtype', 'severity_potential',
];

const EMPTY_WORKER = { name: '', rg: '', visto: '' };

const ANSWER_LABELS = { sim: 'SIM', nao: 'NÃO', na: 'N/A' };

// F-034 / F-035 - Permissões de Trabalho (PTA / PTE)
const PTA_EPI_ITEMS = [
  'Capacete com Jugular', 'Calçado de Segurança', 'Óculos de Segurança',
  'Protetor Auditivo', 'Luva de Segurança', 'Cabo guia (corda)',
  'Cabo guia (aço)', 'Trava Quedas 8mm e 12mm (aço)', 'Trava Quedas 8mm e 12mm (corda)',
  'Absorvedor de energia', 'Mosquetão Rosca', 'Cinto de Segurança Paraquedista',
  'Talabarte Simples', 'Talabarte Duplo Y', 'Ponto de conexão talabarte', 'Mosquetões encaixe rápido',
];

const PTE_EPI_ITEMS = [
  'Capacete com Jugular', 'Calçado de Segurança para NR10', 'Óculos de Segurança',
  'Protetor Auditivo', 'Luva de Segurança', 'Cabo guia (corda)',
  'Luva Pigmentada', 'Trava Quedas 8mm e 12mm (aço)', 'Trava Quedas 8mm e 12mm (corda)',
  'Absorvedor de energia', 'Mosquetão Rosca', 'Cinto de Segurança Paraquedista',
  'Talabarte Simples', 'Talabarte Duplo Y', 'Ponto de conexão talabarte', 'Mosquetões encaixe rápido',
];

const PTA_ENVIRONMENT_ITEMS = [
  'Existem instruções claras para execução do trabalho em altura?',
  'O trabalhador é treinado e está autorizado a realizar o serviço?',
  'A área ao redor do trabalho está bem isolada, identificada e seca, evitando passagem de pessoas e veículos?',
  'A proximidade dos pontos de energia (elétrica, mecânica, pneumática, térmica etc) foi analisada?',
  'A base de apoio como escadas, andaime, rampa, plataformas, etc., possui condições adequadas?',
  'Os meios de acesso elevados possuem resistência suficiente para aguentar a carga de trabalho a qual estariam sujeitos?',
  'Os meios de acesso elevados apresentam condições adequadas para uso?',
  'O ponto onde o talabarte do cinto de segurança está preso é resistente e seguro?',
  'Existe rede de proteção quando há o risco de queda de objetos?',
  'Existem cabos de aço para colocação de talabarte do cinto de segurança?',
  'Os cabos de aço utilizados possuem bitolas adequadas para a atividade que será utilizado?',
  'As placas de advertência estão nítidas?',
  'Os cabos elétricos visíveis estão em boas condições, existem proteções para evitar contato?',
  'Todos os colaboradores estão de acordo para a execução do serviço?',
  'A equipe foi treinada para o trabalho, está consciente do risco e possuem ASO com aptidão para a atividades?',
  "Existem número suficiente de EPI's para todos os executantes do trabalho?",
  'Os distanciamentos recomendados estão sendo seguidos?',
  'Existe ponto de conexão para o cinto de segurança que está independente da estrutura?',
  'Existe cabo guia para colocação de talabarte?',
  'Energias perigosas que estejam relacionadas com o serviço foram desligadas ou estão protegidas por anteparos?',
  'O local designado para o Monitor do serviço é seguro e permite que o mesmo acompanhe o serviço por todo o tempo?',
  'Os equipamentos de resgate e emergência estão disponíveis?',
  'O local é classificado como Espaço Confinado?',
  'Haverá trabalho a quente no local?',
  'Todos os campos preenchidos pelo executante foram checados e estão atendendo as normas de seguridade, conforme assinalado.',
];

const PTE_ENVIRONMENT_ITEMS = [
  'No local do Trabalho a Quente foi identificado a possível presença de Gases / Vapores Inflamáveis?',
  'Foi providenciado dispositivos de proteção contra princípios de incêndio, inclusive extintores de incêndio?',
  'Foi verificado no local a ausência de materiais combustíveis?',
  'Na operação de soldagem e corte a quente, é utilizado anteparo eficaz para a proteção contra respingos sendo o material utilizado nesta proteção do tipo incombustível?',
  'Nas operações de soldagem ou corte a quente de vasilhame, tanque ou similar, que envolvam geração de gases confinados ou semiconfinados, foram adotadas medidas para eliminar riscos de explosão e intoxicação do colaborador?',
  'As mangueiras dos conjuntos oxi-corte possuem válvulas de retrocesso das chamas na saída do cilindro e chegada do maçarico?',
  'As mangueiras estão em boas condições (estanqueidade, desgaste ou deterioração) e livre de emendas não conformes?',
  'Todos os equipamentos a serem utilizados possuem check list?',
  'Foi verificado se os bloqueios das energias estão corretos e a realização do TESTE DE ENERGIA ZERO?',
  'Foram verificados os EPIs Especiais necessários para a realização dos bloqueios das energias?',
  'Foi programado o TESTE DE ENERGIA ZERO, na presença do Executante, Responsável pela atividade?',
  'Foi orientado aos colaboradores de que qualquer mudança no bloqueio de energia, a atividade deve ser paralisada e realizada a abertura de uma nova PTR - PERMISSÃO DE TRABALHO DE RISCO?',
  'A equipe foi treinada para o trabalho, está consciente do risco e possuem ASO com aptidão para a atividades?',
  "Existem número suficiente de EPI's para todos os executantes do trabalho?",
  'Os distanciamentos recomendados estão sendo seguidos e a área isolada e sinalizada devidamente?',
  'Existe ponto de conexão para o cinto de segurança que está independente da estrutura?',
  'Existe cabo guia para colocação de talabarte?',
  'Energias perigosas que estejam relacionadas com o serviço foram desligadas ou estão protegidas por anteparos?',
  'O local designado para o Monitor do serviço é seguro e permite que o mesmo acompanhe o serviço por todo o tempo?',
  'Os equipamentos de resgate e emergência estão disponíveis?',
  'O local é classificado como Espaço Confinado?',
  'Haverá trabalho a quente no local?',
  'Todos os campos preenchidos pelo executante foram checados e estão atendendo as normas de seguridade, conforme assinalado?',
];

const PERMIT_DEFINITIONS = {
  pta: {
    type: 'pta',
    title: 'PTA - Permissão para Trabalho em Altura',
    formCode: 'F-034',
    workTypes: ['Plataforma Elevatória', 'Cadeira Suspensa', 'Escada', 'Andaime', 'Telhado'],
    epiItems: PTA_EPI_ITEMS,
    environmentItems: PTA_ENVIRONMENT_ITEMS,
    controlMeasuresAfterIndex: null,
  },
  pte: {
    type: 'pte',
    title: 'PTE - Permissão para Trabalho Especial',
    formCode: 'F-035',
    workTypes: ['Plataforma Elevatória', 'Cadeira Suspensa', 'Escada', 'Andaime', 'Telhado', 'Eletricidade'],
    epiItems: PTE_EPI_ITEMS,
    environmentItems: PTE_ENVIRONMENT_ITEMS,
    controlMeasuresAfterIndex: 12,
  },
};

const SST = () => {
  const [activeTab, setActiveTab] = useState('occurrences');
  const [occurrences, setOccurrences] = useState([]);
  const [epis, setEpis] = useState([]);
  const [workPermits, setWorkPermits] = useState([]);
  const [occurrenceStats, setOccurrenceStats] = useState({ total_year: 0, open: 0, days_lost: 0, by_type: {}, by_severity: {} });
  const [epiStats, setEpiStats] = useState({ total: 0, low_stock: 0, out_of_stock: 0, ok: 0 });
  const [loading, setLoading] = useState(true);
  const [showOccurrenceModal, setShowOccurrenceModal] = useState(false);
  const [showEpiModal, setShowEpiModal] = useState(false);
  const [showChooserModal, setShowChooserModal] = useState(false);
  const [showWorkPermitModal, setShowWorkPermitModal] = useState(false);
  const [showViewPermitModal, setShowViewPermitModal] = useState(false);
  const [showViewOccurrenceModal, setShowViewOccurrenceModal] = useState(false);
  const [creatingPermitType, setCreatingPermitType] = useState('pta');
  const [editingOccurrence, setEditingOccurrence] = useState(null);
  const [viewingOccurrence, setViewingOccurrence] = useState(null);
  const [editingEpi, setEditingEpi] = useState(null);
  const [editingWorkPermit, setEditingWorkPermit] = useState(null);
  const [viewingWorkPermit, setViewingWorkPermit] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approvingOccurrence, setApprovingOccurrence] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveringEpi, setDeliveringEpi] = useState(null);
  const [showDeliveriesModal, setShowDeliveriesModal] = useState(false);
  const [viewingDeliveriesEpi, setViewingDeliveriesEpi] = useState(null);
  const [epiDeliveries, setEpiDeliveries] = useState([]);
  const [filters, setFilters] = useState({ occurrence_type: '', severity: '', status: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'occurrences') {
        const [occRes, statsRes] = await Promise.all([
          sstApi.listOccurrences(filters),
          sstApi.occurrenceStats(),
        ]);
        setOccurrences(occRes.data);
        setOccurrenceStats(statsRes.data);
      } else if (activeTab === 'epis') {
        const [episRes, statsRes, permitsRes] = await Promise.all([
          sstApi.listEpis(),
          sstApi.epiStats(),
          sstApi.listWorkPermits(),
        ]);
        setEpis(episRes.data);
        setEpiStats(statsRes.data);
        setWorkPermits(permitsRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOccurrence = async (data) => {
    try {
      await sstApi.createOccurrence(data);
      toast.success('Ocorrência registrada com sucesso');
      setShowOccurrenceModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar ocorrência'));
    }
  };

  const handleUpdateOccurrence = async (data) => {
    try {
      await sstApi.updateOccurrence(editingOccurrence.id, data);
      toast.success('Ocorrência atualizada');
      setShowOccurrenceModal(false);
      setEditingOccurrence(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleEditOccurrence = async (occ) => {
    try {
      const res = await sstApi.getOccurrence(occ.id);
      setEditingOccurrence(res.data);
      setShowOccurrenceModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar ocorrência'));
    }
  };

  const handleViewOccurrence = async (occ) => {
    try {
      const res = await sstApi.getOccurrence(occ.id);
      setViewingOccurrence(res.data);
      setShowViewOccurrenceModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar ocorrência'));
    }
  };

  const handleApproveOccurrence = async (id) => {
    setApprovingOccurrence(true);
    try {
      const res = await sstApi.approveOccurrence(id);
      setViewingOccurrence(res.data);
      toast.success('Ocorrência aprovada');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao aprovar'));
    } finally {
      setApprovingOccurrence(false);
    }
  };

  const handleCreateEpi = async (data) => {
    try {
      await sstApi.createEpi(data);
      toast.success('EPI cadastrado com sucesso');
      setShowEpiModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar EPI'));
    }
  };

  const handleUpdateEpi = async (data) => {
    try {
      await sstApi.updateEpi(editingEpi.id, data);
      toast.success('EPI atualizado');
      setShowEpiModal(false);
      setEditingEpi(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleOpenDelivery = (epi) => {
    setDeliveringEpi(epi);
    setShowDeliveryModal(true);
  };

  const handleCreateDelivery = async (data) => {
    try {
      await sstApi.createDelivery({ ...data, epi_id: deliveringEpi.id });
      toast.success('Entrega de EPI registrada com sucesso');
      setShowDeliveryModal(false);
      setDeliveringEpi(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao registrar entrega'));
    }
  };

  const handleViewDeliveries = async (epi) => {
    try {
      const res = await sstApi.listDeliveries(epi.id);
      setEpiDeliveries(res.data);
      setViewingDeliveriesEpi(epi);
      setShowDeliveriesModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar entregas'));
    }
  };

  const handleCreateWorkPermit = async (data) => {
    try {
      await sstApi.createWorkPermit(data);
      toast.success('Permissão de trabalho registrada com sucesso');
      setShowWorkPermitModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar permissão de trabalho'));
    }
  };

  const handleUpdateWorkPermit = async (data) => {
    try {
      await sstApi.updateWorkPermit(editingWorkPermit.id, data);
      toast.success('Permissão de trabalho atualizada');
      setShowWorkPermitModal(false);
      setEditingWorkPermit(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleEditWorkPermit = async (permit) => {
    try {
      const res = await sstApi.getWorkPermit(permit.id);
      setEditingWorkPermit(res.data);
      setShowWorkPermitModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar permissão de trabalho'));
    }
  };

  const handleViewWorkPermit = async (permit) => {
    try {
      const res = await sstApi.getWorkPermit(permit.id);
      setViewingWorkPermit(res.data);
      setShowViewPermitModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar permissão de trabalho'));
    }
  };

  const handleApproveWorkPermit = async (id) => {
    setApproving(true);
    try {
      const res = await sstApi.approveWorkPermit(id);
      setViewingWorkPermit(res.data);
      toast.success('Permissão de trabalho aprovada');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao aprovar'));
    } finally {
      setApproving(false);
    }
  };

  const handleChooseCreate = (choice) => {
    setShowChooserModal(false);
    if (choice === 'epi') {
      setEditingEpi(null);
      setShowEpiModal(true);
    } else {
      setEditingWorkPermit(null);
      setCreatingPermitType(choice);
      setShowWorkPermitModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.type === 'occurrence') {
        await sstApi.deleteOccurrence(confirmDelete.id);
        toast.success('Ocorrência excluída');
      } else if (confirmDelete.type === 'permit') {
        await sstApi.deleteWorkPermit(confirmDelete.id);
        toast.success('Permissão de trabalho excluída');
      } else {
        await sstApi.deleteEpi(confirmDelete.id);
        toast.success('EPI excluído');
      }
      setConfirmDelete(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir'));
    }
  };

  const tabs = [
    { id: 'occurrences', label: 'Ocorrências', icon: AlertTriangle },
    { id: 'epis', label: 'EPIs', icon: Shield },
    { id: 'indicators', label: 'Indicadores', icon: BarChart3 },
  ];

  return (
    <div className="space-y-6" data-testid="sst-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3"><Shield className="text-green-500" />Gestão de SST</h1>
          <p className="text-gray-400 mt-1">Saúde e Segurança do Trabalho</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'occurrences' && (
            <button
              onClick={() => { setEditingOccurrence(null); setShowOccurrenceModal(true); }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-occurrence-btn"
            >
              <Plus size={20} />
              Nova Ocorrência
            </button>
          )}
          {activeTab === 'epis' && (
            <button
              onClick={() => setShowChooserModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-epi-btn"
            >
              <Plus size={20} />
              Novo EPI
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {activeTab === 'occurrences' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4"><p className="text-gray-400 text-sm">Total do Ano</p><p className="text-2xl font-bold text-white">{occurrenceStats.total_year}</p></div>
          <div className="card p-4"><p className="text-gray-400 text-sm">Em Aberto</p><p className="text-2xl font-bold text-white">{occurrenceStats.open}</p></div>
          <div className="card p-4"><p className="text-gray-400 text-sm">Dias Perdidos</p><p className="text-2xl font-bold text-white">{occurrenceStats.days_lost}</p></div>
          <div className="card p-4"><p className="text-gray-400 text-sm">Acidentes</p><p className="text-2xl font-bold text-white">{occurrenceStats.by_type?.accident || 0}</p></div>
        </div>
      )}

      {activeTab === 'epis' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4"><p className="text-gray-400 text-sm">Total de EPIs</p><p className="text-2xl font-bold text-white">{epiStats.total}</p></div>
          <div className="card p-4"><p className="text-gray-400 text-sm">Estoque OK</p><p className="text-2xl font-bold text-white">{epiStats.ok}</p></div>
          <div className="card p-4"><p className="text-gray-400 text-sm">Estoque Baixo</p><p className="text-2xl font-bold text-white">{epiStats.low_stock}</p></div>
          <div className="card p-4"><p className="text-gray-400 text-sm">Sem Estoque</p><p className="text-2xl font-bold text-white">{epiStats.out_of_stock}</p></div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-green-400 text-green-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : activeTab === 'occurrences' ? (
          <OccurrencesTable
            occurrences={occurrences}
            onView={handleViewOccurrence}
            onApprove={(occ) => handleApproveOccurrence(occ.id)}
            onEdit={handleEditOccurrence}
            onDelete={(occ) => setConfirmDelete({ type: 'occurrence', id: occ.id, name: occ.code })}
            filters={filters}
            setFilters={setFilters}
          />
        ) : activeTab === 'epis' ? (
          <EPIsTable
            epis={epis}
            onEdit={(epi) => { setEditingEpi(epi); setShowEpiModal(true); }}
            onDelete={(epi) => setConfirmDelete({ type: 'epi', id: epi.id, name: epi.name })}
            onDeliver={handleOpenDelivery}
            onViewDeliveries={handleViewDeliveries}
          />
        ) : (
          <IndicatorsView />
        )}
      </div>

      {/* Permissões de Trabalho (PTA / PTE) */}
      {activeTab === 'epis' && !loading && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <HardHat size={18} className="text-green-500" />
              Permissões de Trabalho (PTA / PTE)
            </h3>
          </div>
          <WorkPermitsTable
            permits={workPermits}
            onView={handleViewWorkPermit}
            onEdit={handleEditWorkPermit}
            onDelete={(p) => setConfirmDelete({ type: 'permit', id: p.id, name: p.code })}
          />
        </div>
      )}

      {/* Occurrence Modal */}
      {showOccurrenceModal && (
        <OccurrenceModal
          occurrence={editingOccurrence}
          onClose={() => { setShowOccurrenceModal(false); setEditingOccurrence(null); }}
          onSave={editingOccurrence ? handleUpdateOccurrence : handleCreateOccurrence}
        />
      )}

      {/* Occurrence View / Approve Modal */}
      {showViewOccurrenceModal && viewingOccurrence && (
        <OccurrenceViewModal
          occurrence={viewingOccurrence}
          approving={approvingOccurrence}
          onApprove={() => handleApproveOccurrence(viewingOccurrence.id)}
          onClose={() => { setShowViewOccurrenceModal(false); setViewingOccurrence(null); }}
        />
      )}

      {/* EPI Modal */}
      {showEpiModal && (
        <EPIModal
          epi={editingEpi}
          onClose={() => { setShowEpiModal(false); setEditingEpi(null); }}
          onSave={editingEpi ? handleUpdateEpi : handleCreateEpi}
        />
      )}

      {/* EPI Delivery Modal */}
      {showDeliveryModal && deliveringEpi && (
        <DeliveryModal
          epi={deliveringEpi}
          onClose={() => { setShowDeliveryModal(false); setDeliveringEpi(null); }}
          onSave={handleCreateDelivery}
        />
      )}

      {/* EPI Deliveries History Modal */}
      {showDeliveriesModal && viewingDeliveriesEpi && (
        <DeliveriesHistoryModal
          epi={viewingDeliveriesEpi}
          deliveries={epiDeliveries}
          onClose={() => { setShowDeliveriesModal(false); setViewingDeliveriesEpi(null); setEpiDeliveries([]); }}
        />
      )}

      {/* Chooser: Novo EPI / Nova PTA / Nova PTE */}
      {showChooserModal && (
        <CreateChooserModal
          onClose={() => setShowChooserModal(false)}
          onSelect={handleChooseCreate}
        />
      )}

      {/* Work Permit Modal (PTA / PTE) */}
      {showWorkPermitModal && (
        <WorkPermitModal
          permit={editingWorkPermit}
          permitType={creatingPermitType}
          onClose={() => { setShowWorkPermitModal(false); setEditingWorkPermit(null); }}
          onSave={editingWorkPermit ? handleUpdateWorkPermit : handleCreateWorkPermit}
        />
      )}

      {/* Work Permit View / Approve Modal */}
      {showViewPermitModal && viewingWorkPermit && (
        <WorkPermitViewModal
          permit={viewingWorkPermit}
          approving={approving}
          onApprove={() => handleApproveWorkPermit(viewingWorkPermit.id)}
          onClose={() => { setShowViewPermitModal(false); setViewingWorkPermit(null); }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/20">
                <Trash2 size={22} className="text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Confirmar Exclusão</h3>
            </div>
            <p className="text-gray-400 text-sm">
              Tem certeza que deseja excluir <span className="text-white font-medium">{confirmDelete.name}</span>?
              Esta ação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="btn-secondary"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                <Trash2 size={16} />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OccurrencesTable = ({ occurrences, onView, onApprove, onEdit, onDelete, filters, setFilters }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOccurrences = occurrences.filter(occ =>
    occ.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    occ.affected_person?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (occ) => <td className="font-mono text-white">{occ.code}</td> },
    { key: 'tipo', label: 'Tipo', render: (occ) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${TYPE_LABELS[occ.occurrence_type]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {TYPE_LABELS[occ.occurrence_type]?.label || occ.occurrence_type}
        </span>
      </td>
    ) },
    { key: 'data', label: 'Data', render: (occ) => <td className="text-gray-400">{occ.occurrence_date}</td> },
    { key: 'area', label: 'Área', render: (occ) => <td className="text-gray-400">{occ.area || '-'}</td> },
    { key: 'afetado', label: 'Afetado', render: (occ) => <td className="text-white">{occ.affected_person || '-'}</td> },
    { key: 'severidade', label: 'Severidade', render: (occ) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${SEVERITY_LABELS[occ.severity]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {SEVERITY_LABELS[occ.severity]?.label || occ.severity}
        </span>
      </td>
    ) },
    { key: 'status', label: 'Status', render: (occ) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[occ.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {STATUS_LABELS[occ.status]?.label || occ.status}
        </span>
      </td>
    ) },
    { key: 'dias_perdidos', label: 'Dias Perd.', render: (occ) => <td className="text-gray-400">{occ.days_lost || 0}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (occ) => (
      <td>
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => onView(occ)} className="p-1 text-gray-400 hover:text-green-400" title="Visualizar">
            <Eye size={18} />
          </button>
          {occ.approved_at ? (
            <span className="p-1 text-green-400" title={`Aprovada em ${formatDateBR(occ.approved_at)}`}>
              <CheckCircle2 size={18} />
            </span>
          ) : (
            <button onClick={() => onApprove(occ)} className="p-1 text-gray-400 hover:text-green-400" title="Aprovar">
              <CheckCircle2 size={18} />
            </button>
          )}
          <button onClick={() => onEdit(occ)} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
            <Edit2 size={18} />
          </button>
          <button onClick={() => onDelete(occ)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'sst', columns: COLUMNS });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar ocorrências..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          value={filters.occurrence_type}
          onChange={(e) => setFilters({ ...filters, occurrence_type: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todos os Tipos</option>
          {Object.entries(TYPE_LABELS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todas Severidades</option>
          {Object.entries(SEVERITY_LABELS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todos Status</option>
          {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <div className="flex items-center">
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {visibleColumns.map(col => (
                <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredOccurrences.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="py-8 text-center text-gray-500">Nenhuma ocorrência encontrada</td>
              </tr>
            ) : (
              filteredOccurrences.map((occ) => (
                <tr key={occ.id}>
                  {visibleColumns.map(col => (
                    <Fragment key={col.key}>{col.render(occ)}</Fragment>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EPIsTable = ({ epis, onEdit, onDelete, onDeliver, onViewDeliveries }) => (
  <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Código</th>
          <th>Nome</th>
          <th>Tipo</th>
          <th>Estoque Atual</th>
          <th>Estoque Mín.</th>
          <th>Status</th>
          <th>Validade</th>
          <th className="text-right">Ações</th>
        </tr>
      </thead>
      <tbody>
        {epis.length === 0 ? (
          <tr>
            <td colSpan={8} className="py-8 text-center text-gray-500">Nenhum EPI cadastrado</td>
          </tr>
        ) : (
          epis.map((epi) => (
            <tr key={epi.id}>
              <td className="font-mono text-white">{epi.code}</td>
              <td className="text-white">{epi.name}</td>
              <td className="text-gray-400">{epi.epi_type || '-'}</td>
              <td className="text-white">{epi.current_stock}</td>
              <td className="text-gray-400">{epi.minimum_stock}</td>
              <td>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  epi.stock_status === 'ok' ? 'bg-green-500/20 text-green-400' :
                  epi.stock_status === 'low' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {epi.stock_status === 'ok' ? 'OK' : epi.stock_status === 'low' ? 'Baixo' : 'Crítico'}
                </span>
              </td>
              <td>
                {epi.validity_status ? (
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      epi.validity_status === 'expired' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                    }`}
                    title={epi.expiry_date ? formatDateBR(epi.expiry_date) : ''}
                  >
                    {epi.validity_status === 'expired' ? 'Vencido' : 'OK'}
                  </span>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </td>
              <td>
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => onDeliver(epi)}
                    className="p-1 text-gray-400 hover:text-purple-400 disabled:opacity-30 disabled:hover:text-gray-400"
                    title="Registrar Entrega"
                    disabled={epi.current_stock <= 0}
                  >
                    <Package size={18} />
                  </button>
                  <button onClick={() => onViewDeliveries(epi)} className="p-1 text-gray-400 hover:text-green-400" title="Histórico de Entregas">
                    <Eye size={18} />
                  </button>
                  <button onClick={() => onEdit(epi)} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => onDelete(epi)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const CreateChooserModal = ({ onClose, onSelect }) => (
  <>
    <div className="modal-backdrop" />
    <div className="modal-content p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">O que deseja registrar?</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={24} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => onSelect('epi')}
          className="card p-5 text-left border border-transparent hover:border-green-500 transition-colors"
        >
          <Shield className="text-green-400 mb-3" size={28} />
          <p className="text-white font-semibold">Novo EPI</p>
          <p className="text-gray-400 text-sm mt-1">Cadastrar equipamento de proteção individual</p>
        </button>
        <button
          onClick={() => onSelect('pta')}
          className="card p-5 text-left border border-transparent hover:border-green-500 transition-colors"
        >
          <HardHat className="text-green-400 mb-3" size={28} />
          <p className="text-white font-semibold">Nova PTA</p>
          <p className="text-gray-400 text-sm mt-1">F-034 - Permissão para Trabalho em Altura</p>
        </button>
        <button
          onClick={() => onSelect('pte')}
          className="card p-5 text-left border border-transparent hover:border-green-500 transition-colors"
        >
          <AlertTriangle className="text-green-400 mb-3" size={28} />
          <p className="text-white font-semibold">Nova PTE</p>
          <p className="text-gray-400 text-sm mt-1">F-035 - Permissão para Trabalho Especial</p>
        </button>
      </div>
    </div>
  </>
);

const PERMIT_TYPE_LABELS = {
  pta: { label: 'PTA', color: 'bg-blue-500/20 text-blue-400' },
  pte: { label: 'PTE', color: 'bg-purple-500/20 text-purple-400' },
};

const PERMIT_STATUS_LABELS = {
  open: { label: 'Aberta', color: 'bg-blue-500/20 text-blue-400' },
  closed: { label: 'Encerrada', color: 'bg-green-500/20 text-green-400' },
};

const WorkPermitsTable = ({ permits, onView, onEdit, onDelete }) => (
  <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Código</th>
          <th>Tipo</th>
          <th>Executante</th>
          <th>Data</th>
          <th>Local</th>
          <th>Status</th>
          <th>Aprovação</th>
          <th className="text-right">Ações</th>
        </tr>
      </thead>
      <tbody>
        {permits.length === 0 ? (
          <tr>
            <td colSpan={8} className="py-8 text-center text-gray-500">Nenhuma permissão de trabalho registrada</td>
          </tr>
        ) : (
          permits.map((p) => (
            <tr key={p.id}>
              <td className="font-mono text-white">{p.code}</td>
              <td>
                <span className={`px-2 py-1 rounded-full text-xs ${PERMIT_TYPE_LABELS[p.permit_type]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                  {PERMIT_TYPE_LABELS[p.permit_type]?.label || p.permit_type}
                </span>
              </td>
              <td className="text-white">{p.executant || '-'}</td>
              <td className="text-gray-400">{p.work_date || '-'}</td>
              <td className="text-gray-400">{p.location_description || '-'}</td>
              <td>
                <span className={`px-2 py-1 rounded-full text-xs ${PERMIT_STATUS_LABELS[p.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                  {PERMIT_STATUS_LABELS[p.status]?.label || p.status}
                </span>
              </td>
              <td>
                {p.approved_at ? (
                  <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400" title={p.approved_by || ''}>
                    Aprovada
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">Pendente</span>
                )}
              </td>
              <td>
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => onView(p)} className="p-1 text-gray-400 hover:text-green-400" title="Visualizar">
                    <Eye size={18} />
                  </button>
                  <button onClick={() => onEdit(p)} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => onDelete(p)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const WorkPermitModal = ({ permit, permitType, onClose, onSave }) => {
  const definition = PERMIT_DEFINITIONS[permit?.permit_type || permitType];

  const buildEpiChecklist = () => definition.epiItems.map((label) => {
    const existing = permit?.epi_checklist?.find((i) => i.label === label);
    return { label, checked: existing?.checked || false };
  });

  const buildEnvironmentChecklist = () => definition.environmentItems.map((question) => {
    const existing = permit?.environment_checklist?.find((i) => i.question === question);
    return { question, answer: existing?.answer || '' };
  });

  const [formData, setFormData] = useState({
    work_date: permit?.work_date || new Date().toISOString().split('T')[0],
    start_time: permit?.start_time || '',
    end_time: permit?.end_time || '',
    executant: permit?.executant || '',
    work_types: permit?.work_types || [],
    location_description: permit?.location_description || '',
    requires_observer: permit?.requires_observer ?? null,
    activity_description: permit?.activity_description || '',
    labor_type: permit?.labor_type || '',
    people_count: permit?.people_count ?? '',
    epi_verified_confirmation: permit?.epi_verified_confirmation || false,
    control_measures: permit?.control_measures || '',
    responsible_service_name: permit?.responsible_service_name || '',
    responsible_service_signature: permit?.responsible_service_signature || '',
    responsible_sesmt_name: permit?.responsible_sesmt_name || '',
    responsible_sesmt_signature: permit?.responsible_sesmt_signature || '',
    return_date: permit?.return_date || '',
    return_time: permit?.return_time || '',
    operator_name: permit?.operator_name || '',
    operator_signature: permit?.operator_signature || '',
    closure_sesmt_name: permit?.closure_sesmt_name || '',
    closure_sesmt_signature: permit?.closure_sesmt_signature || '',
    observation: permit?.observation || '',
    prepared_by: permit?.prepared_by || '',
    approved_by: permit?.approved_by || '',
    status: permit?.status || 'open',
  });
  const [epiChecklist, setEpiChecklist] = useState(buildEpiChecklist());
  const [environmentChecklist, setEnvironmentChecklist] = useState(buildEnvironmentChecklist());
  const [workers, setWorkers] = useState(
    permit?.workers?.length
      ? permit.workers.map((w) => ({ name: w.name || '', rg: w.rg || '', visto: w.visto || '' }))
      : [{ ...EMPTY_WORKER }]
  );
  const [saving, setSaving] = useState(false);

  const toggleWorkType = (label) => {
    setFormData((f) => ({
      ...f,
      work_types: f.work_types.includes(label) ? f.work_types.filter((t) => t !== label) : [...f.work_types, label],
    }));
  };

  const toggleEpiItem = (idx) => {
    setEpiChecklist((rows) => rows.map((r, i) => (i === idx ? { ...r, checked: !r.checked } : r)));
  };

  const setEnvironmentAnswer = (idx, answer) => {
    setEnvironmentChecklist((rows) => rows.map((r, i) => (i === idx ? { ...r, answer } : r)));
  };

  const updateWorker = (idx, field, value) => {
    setWorkers((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addWorker = () => setWorkers((rows) => [...rows, { ...EMPTY_WORKER }]);
  const removeWorker = (idx) => setWorkers((rows) => rows.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.work_date, label: 'Data' },
      { value: formData.return_date, label: 'Data de Devolução' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    const payload = {
      ...formData,
      permit_type: definition.type,
      people_count: formData.people_count === '' ? null : (parseInt(formData.people_count) || null),
      work_date: formData.work_date || null,
      return_date: formData.return_date || null,
      epi_checklist: epiChecklist,
      environment_checklist: environmentChecklist,
      workers: workers.filter((w) => w.name.trim()),
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">
              {permit ? 'Editar' : 'Nova'} {definition.title}
            </h2>
            <p className="text-xs text-gray-500">{definition.formCode} - {definition.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormSection title="Identificação">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Executante</label>
                <input
                  type="text"
                  value={formData.executant}
                  onChange={(e) => setFormData({ ...formData, executant: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data</label>
                <input
                  type="date"
                  value={formData.work_date}
                  onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Início</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fim</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Localização do Serviço</label>
                <input
                  type="text"
                  value={formData.location_description}
                  onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">N. de Pessoas</label>
                <input
                  type="number"
                  min="0"
                  value={formData.people_count}
                  onChange={(e) => setFormData({ ...formData, people_count: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-300 mb-2">Requer Observador</span>
                <div className="flex gap-4">
                  <RadioOption name="requires_observer" value={true} current={formData.requires_observer} onChange={(v) => setFormData({ ...formData, requires_observer: v })} label="SIM" />
                  <RadioOption name="requires_observer" value={false} current={formData.requires_observer} onChange={(v) => setFormData({ ...formData, requires_observer: v })} label="NÃO" />
                </div>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-300 mb-2">Mão de Obra</span>
                <div className="flex gap-4">
                  <RadioOption name="labor_type" value="interna" current={formData.labor_type} onChange={(v) => setFormData({ ...formData, labor_type: v })} label="Interna" />
                  <RadioOption name="labor_type" value="externa" current={formData.labor_type} onChange={(v) => setFormData({ ...formData, labor_type: v })} label="Externa" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição da Atividade</label>
              <textarea
                value={formData.activity_description}
                onChange={(e) => setFormData({ ...formData, activity_description: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
          </FormSection>

          <FormSection title="Tipo de Trabalho">
            <div className="flex flex-wrap gap-4">
              {definition.workTypes.map((wt) => (
                <label key={wt} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.work_types.includes(wt)}
                    onChange={() => toggleWorkType(wt)}
                    className="form-checkbox text-green-500"
                  />
                  {wt}
                </label>
              ))}
            </div>
          </FormSection>

          <FormSection title="Lista de Verificação - Pessoas e Equipamento de Proteção Individual - EPIs">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
              {epiChecklist.map((item, idx) => (
                <label key={item.label} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => toggleEpiItem(idx)}
                    className="form-checkbox text-green-500"
                  />
                  {item.label}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer pt-3 mt-2 border-t border-gray-700">
              <input
                type="checkbox"
                checked={formData.epi_verified_confirmation}
                onChange={(e) => setFormData({ ...formData, epi_verified_confirmation: e.target.checked })}
                className="form-checkbox text-green-500"
              />
              Os EPIs foram pessoalmente verificados e estão em boas condições para realização do trabalho
            </label>
          </FormSection>

          <FormSection title="Lista de Verificação - Ambiente de Trabalho pelo Executante">
            <div className="space-y-1">
              {environmentChecklist.map((item, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-2 border-b border-gray-800">
                    <p className="text-sm text-gray-300 flex-1">{item.question}</p>
                    <div className="flex gap-3 shrink-0">
                      {Object.entries(ANSWER_LABELS).map(([key, label]) => (
                        <RadioOption
                          key={key}
                          name={`env-${idx}`}
                          value={key}
                          current={item.answer}
                          onChange={(v) => setEnvironmentAnswer(idx, v)}
                          label={label}
                        />
                      ))}
                    </div>
                  </div>
                  {definition.controlMeasuresAfterIndex === idx + 1 && (
                    <div className="py-3">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Medidas de Controle</label>
                      <textarea
                        value={formData.control_measures}
                        onChange={(e) => setFormData({ ...formData, control_measures: e.target.value })}
                        className="input w-full"
                        rows={2}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </FormSection>

          <FormSection title="Servidores/Trabalhadores que Participarão da Tarefa">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>RG</th>
                    <th>Visto</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {workers.map((worker, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          value={worker.name}
                          onChange={(e) => updateWorker(idx, 'name', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={worker.rg}
                          onChange={(e) => updateWorker(idx, 'rg', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={worker.visto}
                          onChange={(e) => updateWorker(idx, 'visto', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => removeWorker(idx)}
                          className="p-1 text-gray-400 hover:text-red-400"
                          title="Remover"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addWorker}
              className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300"
            >
              <Plus size={16} /> Adicionar Trabalhador
            </button>
          </FormSection>

          <FormSection title="Responsáveis pela Emissão">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nome do Responsável pelo Serviço</label>
                <input
                  type="text"
                  value={formData.responsible_service_name}
                  onChange={(e) => setFormData({ ...formData, responsible_service_name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assinatura</label>
                <input
                  type="text"
                  value={formData.responsible_service_signature}
                  onChange={(e) => setFormData({ ...formData, responsible_service_signature: e.target.value })}
                  className="input w-full"
                  placeholder="Nome de confirmação (assinatura eletrônica não disponível)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nome Responsável do SESMT pela Emissão da PT</label>
                <input
                  type="text"
                  value={formData.responsible_sesmt_name}
                  onChange={(e) => setFormData({ ...formData, responsible_sesmt_name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assinatura</label>
                <input
                  type="text"
                  value={formData.responsible_sesmt_signature}
                  onChange={(e) => setFormData({ ...formData, responsible_sesmt_signature: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Devolução / Encerramento">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data</label>
                <input
                  type="date"
                  value={formData.return_date}
                  onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Hora</label>
                <input
                  type="time"
                  value={formData.return_time}
                  onChange={(e) => setFormData({ ...formData, return_time: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nome Operador do Serviço</label>
                <input
                  type="text"
                  value={formData.operator_name}
                  onChange={(e) => setFormData({ ...formData, operator_name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assinatura</label>
                <input
                  type="text"
                  value={formData.operator_signature}
                  onChange={(e) => setFormData({ ...formData, operator_signature: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nome Responsável de SESMT</label>
                <input
                  type="text"
                  value={formData.closure_sesmt_name}
                  onChange={(e) => setFormData({ ...formData, closure_sesmt_name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assinatura</label>
                <input
                  type="text"
                  value={formData.closure_sesmt_signature}
                  onChange={(e) => setFormData({ ...formData, closure_sesmt_signature: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Observação</label>
              <textarea
                value={formData.observation}
                onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
          </FormSection>

          <FormSection title="Elaboração / Aprovação">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Elaborado por</label>
                <input
                  type="text"
                  value={formData.prepared_by}
                  onChange={(e) => setFormData({ ...formData, prepared_by: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Aprovado por</label>
                <input
                  type="text"
                  value={formData.approved_by}
                  onChange={(e) => setFormData({ ...formData, approved_by: e.target.value })}
                  className="input w-full"
                />
              </div>
              {permit && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input w-full"
                  >
                    {Object.entries(PERMIT_STATUS_LABELS).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const ViewRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-1.5 border-b border-gray-800 text-sm">
    <span className="text-gray-400">{label}</span>
    <span className="text-white sm:text-right">{value || '-'}</span>
  </div>
);

const ANSWER_BADGE_COLORS = {
  sim: 'bg-green-500/20 text-green-400',
  nao: 'bg-red-500/20 text-red-400',
  na: 'bg-gray-500/20 text-gray-400',
};

const WorkPermitViewModal = ({ permit, approving, onApprove, onClose }) => {
  const definition = PERMIT_DEFINITIONS[permit.permit_type];
  const isApproved = !!permit.approved_at;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {permit.code}
              <span className={`px-2 py-1 rounded-full text-xs ${PERMIT_TYPE_LABELS[permit.permit_type]?.color}`}>
                {PERMIT_TYPE_LABELS[permit.permit_type]?.label}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs ${PERMIT_STATUS_LABELS[permit.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                {PERMIT_STATUS_LABELS[permit.status]?.label || permit.status}
              </span>
            </h2>
            <p className="text-xs text-gray-500">{definition.formCode} - {definition.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {isApproved ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <CheckCircle2 className="text-green-400 shrink-0" size={22} />
              <p className="text-sm text-green-300">
                Aprovado por <span className="font-semibold">{permit.approved_by}</span> em {formatDateBR(permit.approved_at)}.
              </p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-sm text-yellow-300">Esta permissão de trabalho ainda não foi aprovada.</p>
              <button
                type="button"
                onClick={onApprove}
                disabled={approving}
                className="btn-primary flex items-center gap-2 shrink-0"
              >
                {approving ? <><Loader2 size={16} className="animate-spin" /> Aprovando...</> : <><CheckCircle2 size={16} /> Aprovar</>}
              </button>
            </div>
          )}

          <FormSection title="Identificação">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ViewRow label="Executante" value={permit.executant} />
              <ViewRow label="Data" value={formatDateBR(permit.work_date)} />
              <ViewRow label="Início" value={permit.start_time} />
              <ViewRow label="Fim" value={permit.end_time} />
              <ViewRow label="Localização do Serviço" value={permit.location_description} />
              <ViewRow label="N. de Pessoas" value={permit.people_count} />
              <ViewRow label="Requer Observador" value={permit.requires_observer === null ? '-' : (permit.requires_observer ? 'SIM' : 'NÃO')} />
              <ViewRow label="Mão de Obra" value={permit.labor_type === 'interna' ? 'Interna' : permit.labor_type === 'externa' ? 'Externa' : '-'} />
            </div>
            <ViewRow label="Descrição da Atividade" value={permit.activity_description} />
          </FormSection>

          <FormSection title="Tipo de Trabalho">
            <div className="flex flex-wrap gap-2">
              {definition.workTypes.map((wt) => (
                <span
                  key={wt}
                  className={`px-2 py-1 rounded-full text-xs ${permit.work_types?.includes(wt) ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500 line-through'}`}
                >
                  {wt}
                </span>
              ))}
            </div>
          </FormSection>

          <FormSection title="Lista de Verificação - Pessoas e Equipamento de Proteção Individual - EPIs">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
              {definition.epiItems.map((label) => {
                const checked = permit.epi_checklist?.find((i) => i.label === label)?.checked;
                return (
                  <div key={label} className={`flex items-center gap-2 text-sm ${checked ? 'text-white' : 'text-gray-500'}`}>
                    <CheckCircle2 size={14} className={checked ? 'text-green-400' : 'text-gray-600'} />
                    {label}
                  </div>
                );
              })}
            </div>
            {permit.epi_verified_confirmation && (
              <p className="text-sm text-green-400 pt-3 mt-2 border-t border-gray-700 flex items-center gap-2">
                <CheckCircle2 size={14} /> Os EPIs foram pessoalmente verificados e estão em boas condições para realização do trabalho
              </p>
            )}
          </FormSection>

          <FormSection title="Lista de Verificação - Ambiente de Trabalho pelo Executante">
            <div className="space-y-1">
              {definition.environmentItems.map((question, idx) => {
                const answer = permit.environment_checklist?.find((i) => i.question === question)?.answer;
                return (
                  <React.Fragment key={idx}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-1.5 border-b border-gray-800">
                      <p className="text-sm text-gray-300 flex-1">{question}</p>
                      <span className={`px-2 py-1 rounded-full text-xs shrink-0 ${ANSWER_BADGE_COLORS[answer] || 'bg-gray-700/40 text-gray-500'}`}>
                        {ANSWER_LABELS[answer] || '-'}
                      </span>
                    </div>
                    {definition.controlMeasuresAfterIndex === idx + 1 && (
                      <ViewRow label="Medidas de Controle" value={permit.control_measures} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </FormSection>

          <FormSection title="Servidores/Trabalhadores que Participarão da Tarefa">
            {permit.workers?.length ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr><th>Nome</th><th>RG</th><th>Visto</th></tr>
                  </thead>
                  <tbody>
                    {permit.workers.map((w, idx) => (
                      <tr key={idx}>
                        <td className="text-white">{w.name}</td>
                        <td className="text-gray-400">{w.rg || '-'}</td>
                        <td className="text-gray-400">{w.visto || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nenhum trabalhador informado</p>
            )}
          </FormSection>

          <FormSection title="Responsáveis pela Emissão">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ViewRow label="Nome do Responsável pelo Serviço" value={permit.responsible_service_name} />
              <ViewRow label="Assinatura" value={permit.responsible_service_signature} />
              <ViewRow label="Nome Responsável do SESMT pela Emissão da PT" value={permit.responsible_sesmt_name} />
              <ViewRow label="Assinatura" value={permit.responsible_sesmt_signature} />
            </div>
          </FormSection>

          <FormSection title="Devolução / Encerramento">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ViewRow label="Data" value={formatDateBR(permit.return_date)} />
              <ViewRow label="Hora" value={permit.return_time} />
              <ViewRow label="Nome Operador do Serviço" value={permit.operator_name} />
              <ViewRow label="Assinatura" value={permit.operator_signature} />
              <ViewRow label="Nome Responsável de SESMT" value={permit.closure_sesmt_name} />
              <ViewRow label="Assinatura" value={permit.closure_sesmt_signature} />
            </div>
            <ViewRow label="Observação" value={permit.observation} />
          </FormSection>

          <FormSection title="Elaboração / Aprovação">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ViewRow label="Elaborado por" value={permit.prepared_by} />
              <ViewRow label="Aprovado por" value={permit.approved_by} />
            </div>
          </FormSection>
        </div>

        <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-700">
          <button type="button" onClick={onClose} className="btn-secondary">Fechar</button>
        </div>
      </div>
    </>
  );
};

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const INDICATOR_CUSTOM_TOOLTIP = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#14233D', border: '1px solid rgba(37,99,235,0.3)', padding: '0.5rem 0.75rem', borderRadius: '0.5rem' }}>
        <p className="text-white text-xs font-medium mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const IndicatorsView = () => {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    loadIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const loadIndicators = async () => {
    setLoading(true);
    try {
      const res = await sstApi.listIndicators({ year });
      setIndicators(res.data);
    } catch (error) {
      console.error('Error loading indicators:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      await sstApi.createIndicator(data);
      toast.success('Indicador salvo com sucesso');
      setShowModal(false);
      setEditingIndicator(null);
      await loadIndicators();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar indicador'));
    }
  };

  // Ordena cronologicamente (Jan -> Dez) para os gráficos de evolução mensal
  const chartData = [...indicators]
    .sort((a, b) => a.month - b.month)
    .map((ind) => ({
      month: `${MONTH_NAMES[ind.month - 1].slice(0, 3)}/${String(ind.year).slice(-2)}`,
      'Ac. c/ Afast.': ind.accidents_with_leave || 0,
      'Ac. s/ Afast.': ind.accidents_without_leave || 0,
      'Incidentes': ind.incidents || 0,
      'Quase Ac.': ind.near_misses || 0,
      'Dias Perd.': ind.days_lost || 0,
      'T. Frequência': ind.frequency_rate || 0,
      'T. Gravidade': ind.severity_rate || 0,
    }));

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Indicadores de SST</h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-lg border border-gray-700 overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'table' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:text-white'
                }`}
                title="Visualizar em tabela"
              >
                <Table2 size={16} />
                Tabela
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'chart' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:text-white'
                }`}
                title="Visualizar em gráfico"
              >
                <LineChartIcon size={16} />
                Gráfico
              </button>
            </div>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="input py-1.5 px-3 text-sm"
            >
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={() => { setEditingIndicator(null); setShowModal(true); }}
              className="btn-primary"
            >
              <Plus size={18} />
              Novo Indicador
            </button>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-green-500" size={32} /></div>
        ) : indicators.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Nenhum indicador registrado para este ano</p>
        ) : viewMode === 'chart' ? (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-3">Ocorrências por Mês</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<INDICATOR_CUSTOM_TOOLTIP />} />
                  <Legend formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>} />
                  <Bar dataKey="Ac. c/ Afast." fill="#ef4444" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Ac. s/ Afast." fill="#f97316" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Incidentes" fill="#eab308" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Quase Ac." fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-3">Taxas de Frequência e Gravidade</h4>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip content={<INDICATOR_CUSTOM_TOOLTIP />} />
                  <Legend formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>} />
                  <Line type="monotone" dataKey="T. Frequência" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="T. Gravidade" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-3">Dias Perdidos por Mês</h4>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<INDICATOR_CUSTOM_TOOLTIP />} />
                  <Bar dataKey="Dias Perd." fill="#f97316" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th>Horas Trab.</th>
                  <th>Funcionários</th>
                  <th>Ac. c/ Afast.</th>
                  <th>Ac. s/ Afast.</th>
                  <th>Incidentes</th>
                  <th>Quase Ac.</th>
                  <th>Dias Perd.</th>
                  <th>EPIs Entreg.</th>
                  <th>T. Frequência</th>
                  <th>T. Gravidade</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {indicators.map((ind) => (
                  <tr key={ind.id}>
                    <td className="font-mono text-green-400">{MONTH_NAMES[ind.month - 1]}/{ind.year}</td>
                    <td>{ind.hours_worked?.toLocaleString()}</td>
                    <td>{ind.employees_count}</td>
                    <td>{ind.accidents_with_leave}</td>
                    <td>{ind.accidents_without_leave}</td>
                    <td>{ind.incidents}</td>
                    <td>{ind.near_misses}</td>
                    <td>{ind.days_lost}</td>
                    <td>{ind.epi_deliveries}</td>
                    <td>{ind.frequency_rate?.toFixed(2) || '-'}</td>
                    <td>{ind.severity_rate?.toFixed(2) || '-'}</td>
                    <td>
                      <div className="flex justify-end">
                        <button
                          onClick={() => { setEditingIndicator(ind); setShowModal(true); }}
                          className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                          title="Recalcular / Editar horas e funcionários"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <IndicatorModal
          indicator={editingIndicator}
          defaultYear={year}
          onClose={() => { setShowModal(false); setEditingIndicator(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const IndicatorModal = ({ indicator, defaultYear, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    year: indicator?.year || defaultYear,
    month: indicator?.month || new Date().getMonth() + 1,
    hours_worked: indicator?.hours_worked ?? '',
    employees_count: indicator?.employees_count ?? '',
  });

  useEffect(() => {
    loadPreview(formData.year, formData.month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.year, formData.month]);

  const loadPreview = async (year, month) => {
    setLoadingPreview(true);
    try {
      const res = await sstApi.calculateIndicatorPreview({ year: parseInt(year), month: parseInt(month) });
      setPreview(res.data);
    } catch (error) {
      console.error('Error loading indicator preview:', error);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.hours_worked === '' || formData.employees_count === '') {
      toast.error('Preencha Horas Trabalhadas e Quantidade de Funcionários');
      return;
    }
    setSaving(true);
    try {
      const data = {
        year: parseInt(formData.year),
        month: parseInt(formData.month),
        hours_worked: parseFloat(formData.hours_worked),
        employees_count: parseInt(formData.employees_count),
      };
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {indicator ? 'Editar Indicador de SST' : 'Novo Indicador de SST'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Mês *</label>
              <select
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                className="input w-full"
                required
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Ano *</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Horas Trabalhadas *</label>
              <input
                type="number"
                step="any"
                value={formData.hours_worked}
                onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Quantidade de Funcionários *</label>
              <input
                type="number"
                value={formData.employees_count}
                onChange={(e) => setFormData({ ...formData, employees_count: e.target.value })}
                className="input w-full"
                required
              />
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <p className="text-sm font-medium text-gray-300 mb-3">
              Dados calculados automaticamente a partir das Ocorrências e EPIs do período
            </p>
            {loadingPreview ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="animate-spin text-green-500" size={24} />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Ac. c/ Afastamento</p>
                  <p className="text-lg font-semibold text-white">{preview?.accidents_with_leave ?? '-'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Ac. s/ Afastamento</p>
                  <p className="text-lg font-semibold text-white">{preview?.accidents_without_leave ?? '-'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Incidentes</p>
                  <p className="text-lg font-semibold text-white">{preview?.incidents ?? '-'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Quase Acidentes</p>
                  <p className="text-lg font-semibold text-white">{preview?.near_misses ?? '-'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Dias Perdidos</p>
                  <p className="text-lg font-semibold text-white">{preview?.days_lost ?? '-'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">EPIs Entregues</p>
                  <p className="text-lg font-semibold text-white">{preview?.epi_deliveries ?? '-'}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar Indicador'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const FormSection = ({ title, children }) => (
  <div className="border border-gray-700 rounded-lg p-4 space-y-3">
    <h3 className="text-xs font-semibold text-green-400 uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

const RadioOption = ({ name, value, current, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
    <input
      type="radio"
      name={name}
      checked={current === value}
      onChange={() => onChange(value)}
      className="form-radio text-green-500"
    />
    {label}
  </label>
);

const OccurrenceModal = ({ occurrence, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    // Cabeçalho
    occurrence_type: occurrence?.occurrence_type || 'incident',
    occurrence_date: occurrence?.occurrence_date || new Date().toISOString().split('T')[0],
    occurrence_time: occurrence?.occurrence_time || '',
    report_date: occurrence?.report_date || new Date().toISOString().split('T')[0],
    location: occurrence?.location || '',
    ros_number: occurrence?.ros_number || '',
    involved_place: occurrence?.involved_place ?? true,
    involved_third_parties: occurrence?.involved_third_parties || false,
    third_parties_name: occurrence?.third_parties_name || '',
    cat_number: occurrence?.cat_number || '',
    generating_situation: occurrence?.generating_situation || '',
    reporter_name: occurrence?.reporter_name || '',
    area: occurrence?.area || '',
    activity: occurrence?.activity || '',
    witnesses: occurrence?.witnesses || '',
    // Detalhamento em caso de acidente
    event_category: occurrence?.event_category || '',
    injury_illness_subtype: occurrence?.injury_illness_subtype || '',
    injury_nature: occurrence?.injury_nature || '',
    injury_causing_object: occurrence?.injury_causing_object || '',
    property_damage_subtype: occurrence?.property_damage_subtype || '',
    property_damage_type: occurrence?.property_damage_type || '',
    property_damage_object: occurrence?.property_damage_object || '',
    environmental_damage_subtype: occurrence?.environmental_damage_subtype || '',
    environmental_damage_nature: occurrence?.environmental_damage_nature || '',
    environmental_damage_object: occurrence?.environmental_damage_object || '',
    incident_subtype: occurrence?.incident_subtype || '',
    incident_object: occurrence?.incident_object || '',
    // Descrição / gravidade / causas / ações / evidências
    description: occurrence?.description || '',
    severity: occurrence?.severity || 'minor',
    severity_potential: occurrence?.severity_potential || '',
    root_cause: occurrence?.root_cause || '',
    preventive_actions: occurrence?.preventive_actions || '',
    evidence_description: occurrence?.evidence_description || '',
    // Consequências / acompanhamento (uso interno do sistema)
    injury_type: occurrence?.injury_type || '',
    body_part: occurrence?.body_part || '',
    days_lost: occurrence?.days_lost || 0,
    medical_treatment: occurrence?.medical_treatment || false,
    hospitalization: occurrence?.hospitalization || false,
    status: occurrence?.status || 'open',
    immediate_actions: occurrence?.immediate_actions || '',
    corrective_actions: occurrence?.corrective_actions || '',
    lessons_learned: occurrence?.lessons_learned || '',
    medical_certificate_data: occurrence?.medical_certificate_data || '',
  });
  const [injuredPersons, setInjuredPersons] = useState(
    occurrence?.injured_persons?.length
      ? occurrence.injured_persons.map((p) => ({
          name: p.name || '',
          registration: p.registration || '',
          age: p.age ?? '',
          function: p.function || '',
          time_in_function: p.time_in_function || '',
        }))
      : [{ ...EMPTY_INJURED_PERSON }]
  );
  const [saving, setSaving] = useState(false);

  const updateInjured = (idx, field, value) => {
    setInjuredPersons((rows) => rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };
  const addInjuredRow = () => setInjuredPersons((rows) => [...rows, { ...EMPTY_INJURED_PERSON }]);
  const removeInjuredRow = (idx) => setInjuredPersons((rows) => rows.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.occurrence_date, label: 'Data do Evento' },
      { value: formData.report_date, label: 'Data do Relatório' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    const validInjured = injuredPersons
      .filter((p) => p.name.trim())
      .map((p) => ({ ...p, age: p.age === '' ? null : parseInt(p.age) || null }));
    const payload = {
      ...formData,
      report_date: formData.report_date || null,
      occurrence_time: formData.occurrence_time || null,
      injured_persons: validInjured,
      affected_person: validInjured[0]?.name || '',
      affected_function: validInjured[0]?.function || '',
    };
    OCCURRENCE_NULLABLE_ENUM_FIELDS.forEach((key) => {
      if (payload[key] === '') payload[key] = null;
    });
    await onSave(payload);
    setSaving(false);
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">
              {occurrence ? 'Editar Ocorrência' : 'Nova Ocorrência'}
            </h2>
            <p className="text-xs text-gray-500">F-029 - Relatório de Ocorrência - SSMA</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Cabeçalho */}
          <FormSection title="Identificação da Ocorrência">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tipo *</label>
                <select
                  value={formData.occurrence_type}
                  onChange={(e) => setFormData({ ...formData, occurrence_type: e.target.value })}
                  className="input w-full"
                  required
                >
                  {Object.entries(TYPE_LABELS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data do Evento *</label>
                <input
                  type="date"
                  value={formData.occurrence_date}
                  onChange={(e) => setFormData({ ...formData, occurrence_date: e.target.value })}
                  className="input w-full"
                  required
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Horário do Evento</label>
                <input
                  type="time"
                  value={formData.occurrence_time}
                  onChange={(e) => setFormData({ ...formData, occurrence_time: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data do Relatório</label>
                <input
                  type="date"
                  value={formData.report_date}
                  onChange={(e) => setFormData({ ...formData, report_date: e.target.value })}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Local da Ocorrência</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
                <input
                  type="text"
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Número da ROS</label>
                <input
                  type="text"
                  value={formData.ros_number}
                  onChange={(e) => setFormData({ ...formData, ros_number: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Número da CAT</label>
                <input
                  type="text"
                  value={formData.cat_number}
                  onChange={(e) => setFormData({ ...formData, cat_number: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Repórter</label>
                <input
                  type="text"
                  value={formData.reporter_name}
                  onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Atividade</label>
                <input
                  type="text"
                  value={formData.activity}
                  onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Testemunhas</label>
                <input
                  type="text"
                  value={formData.witnesses}
                  onChange={(e) => setFormData({ ...formData, witnesses: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-1">
              <span className="text-sm font-medium text-gray-300">Envolvidos:</span>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.involved_place}
                  onChange={(e) => setFormData({ ...formData, involved_place: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Place
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.involved_third_parties}
                  onChange={(e) => setFormData({ ...formData, involved_third_parties: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Terceiros
              </label>
              {formData.involved_third_parties && (
                <input
                  type="text"
                  placeholder="Nome do(s) terceiro(s)"
                  value={formData.third_parties_name}
                  onChange={(e) => setFormData({ ...formData, third_parties_name: e.target.value })}
                  className="input flex-1 min-w-[200px]"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Situação Geradora</label>
              <textarea
                value={formData.generating_situation}
                onChange={(e) => setFormData({ ...formData, generating_situation: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
          </FormSection>

          {/* Tabela de lesionados/envolvidos */}
          <FormSection title="Lesionado(s) / Envolvido(s) no Acidente ou Incidente">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Matrícula (RE)</th>
                    <th>Idade</th>
                    <th>Função</th>
                    <th>Tempo na Função</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {injuredPersons.map((person, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          value={person.name}
                          onChange={(e) => updateInjured(idx, 'name', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={person.registration}
                          onChange={(e) => updateInjured(idx, 'registration', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={person.age}
                          onChange={(e) => updateInjured(idx, 'age', e.target.value)}
                          className="input w-20"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={person.function}
                          onChange={(e) => updateInjured(idx, 'function', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={person.time_in_function}
                          onChange={(e) => updateInjured(idx, 'time_in_function', e.target.value)}
                          className="input w-full"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => removeInjuredRow(idx)}
                          className="p-1 text-gray-400 hover:text-red-400"
                          title="Remover"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addInjuredRow}
              className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300"
            >
              <Plus size={16} /> Adicionar Lesionado/Envolvido
            </button>
          </FormSection>

          {/* Detalhamento em caso de acidente */}
          <FormSection title="Detalhamento em Caso de Acidente">
            <div className="flex flex-wrap gap-4">
              {Object.entries(EVENT_CATEGORY_LABELS).map(([key, label]) => (
                <RadioOption
                  key={key}
                  name="event_category"
                  value={key}
                  current={formData.event_category}
                  onChange={(v) => setFormData({ ...formData, event_category: v })}
                  label={label}
                />
              ))}
            </div>

            {formData.event_category === 'injury_illness' && (
              <div className="space-y-3 pt-2 border-t border-gray-700">
                <div className="flex flex-wrap gap-4">
                  {Object.entries(INJURY_ILLNESS_SUBTYPE_LABELS).map(([key, label]) => (
                    <RadioOption
                      key={key}
                      name="injury_illness_subtype"
                      value={key}
                      current={formData.injury_illness_subtype}
                      onChange={(v) => setFormData({ ...formData, injury_illness_subtype: v })}
                      label={label}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Natureza da Lesão ou Doença</label>
                    <input
                      type="text"
                      value={formData.injury_nature}
                      onChange={(e) => setFormData({ ...formData, injury_nature: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Objeto que Causou a Lesão</label>
                    <input
                      type="text"
                      value={formData.injury_causing_object}
                      onChange={(e) => setFormData({ ...formData, injury_causing_object: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.event_category === 'property_damage' && (
              <div className="space-y-3 pt-2 border-t border-gray-700">
                <div className="flex flex-wrap gap-4">
                  {Object.entries(PROPERTY_DAMAGE_SUBTYPE_LABELS).map(([key, label]) => (
                    <RadioOption
                      key={key}
                      name="property_damage_subtype"
                      value={key}
                      current={formData.property_damage_subtype}
                      onChange={(v) => setFormData({ ...formData, property_damage_subtype: v })}
                      label={label}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
                    <input
                      type="text"
                      value={formData.property_damage_type}
                      onChange={(e) => setFormData({ ...formData, property_damage_type: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Objeto que Causou o Dano</label>
                    <input
                      type="text"
                      value={formData.property_damage_object}
                      onChange={(e) => setFormData({ ...formData, property_damage_object: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.event_category === 'environmental_damage' && (
              <div className="space-y-3 pt-2 border-t border-gray-700">
                <div className="flex flex-wrap gap-4">
                  {Object.entries(ENVIRONMENTAL_DAMAGE_SUBTYPE_LABELS).map(([key, label]) => (
                    <RadioOption
                      key={key}
                      name="environmental_damage_subtype"
                      value={key}
                      current={formData.environmental_damage_subtype}
                      onChange={(v) => setFormData({ ...formData, environmental_damage_subtype: v })}
                      label={label}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Natureza da Perda</label>
                    <input
                      type="text"
                      value={formData.environmental_damage_nature}
                      onChange={(e) => setFormData({ ...formData, environmental_damage_nature: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Objeto Relacionado</label>
                    <input
                      type="text"
                      value={formData.environmental_damage_object}
                      onChange={(e) => setFormData({ ...formData, environmental_damage_object: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.event_category === 'incident' && (
              <div className="space-y-3 pt-2 border-t border-gray-700">
                <div className="flex flex-wrap gap-4">
                  {Object.entries(INCIDENT_SUBTYPE_LABELS).map(([key, label]) => (
                    <RadioOption
                      key={key}
                      name="incident_subtype"
                      value={key}
                      current={formData.incident_subtype}
                      onChange={(v) => setFormData({ ...formData, incident_subtype: v })}
                      label={label}
                    />
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Objeto Relacionado</label>
                  <input
                    type="text"
                    value={formData.incident_object}
                    onChange={(e) => setFormData({ ...formData, incident_object: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>
            )}
          </FormSection>

          {/* Descrição do Evento */}
          <FormSection title="Descrição do Evento">
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={3}
              required
            />
          </FormSection>

          {/* Potencial de Gravidade */}
          <FormSection title="Potencial de Gravidade">
            <div className="flex flex-wrap gap-4">
              {Object.entries(SEVERITY_POTENTIAL_LABELS).map(([key, { label }]) => (
                <RadioOption
                  key={key}
                  name="severity_potential"
                  value={key}
                  current={formData.severity_potential}
                  onChange={(v) => setFormData({ ...formData, severity_potential: v })}
                  label={label}
                />
              ))}
            </div>
          </FormSection>

          {/* Causas Apuradas */}
          <FormSection title="Causas Apuradas">
            <textarea
              value={formData.root_cause}
              onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </FormSection>

          {/* Ações Preventivas */}
          <FormSection title="Ações Preventivas">
            <textarea
              value={formData.preventive_actions}
              onChange={(e) => setFormData({ ...formData, preventive_actions: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </FormSection>

          {/* Evidências Fotográficas */}
          <FormSection title="Evidências Fotográficas">
            <textarea
              value={formData.evidence_description}
              onChange={(e) => setFormData({ ...formData, evidence_description: e.target.value })}
              className="input w-full"
              rows={2}
              placeholder="Descreva as evidências coletadas (fotos, laudos, registros...)"
            />
          </FormSection>

          {/* Consequências e acompanhamento (uso interno) */}
          <FormSection title="Consequências e Acompanhamento">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Severidade (classificação interna)</label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  className="input w-full"
                >
                  {Object.entries(SEVERITY_LABELS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Lesão</label>
                <input
                  type="text"
                  value={formData.injury_type}
                  onChange={(e) => setFormData({ ...formData, injury_type: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Parte do Corpo</label>
                <input
                  type="text"
                  value={formData.body_part}
                  onChange={(e) => setFormData({ ...formData, body_part: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Dias Perdidos</label>
                <input
                  type="number"
                  min="0"
                  value={formData.days_lost}
                  onChange={(e) => setFormData({ ...formData, days_lost: parseInt(e.target.value) || 0 })}
                  className="input w-full"
                />
              </div>
              {occurrence && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input w-full"
                  >
                    {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-gray-400">
                <input
                  type="checkbox"
                  checked={formData.medical_treatment}
                  onChange={(e) => setFormData({ ...formData, medical_treatment: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Atendimento Médico
              </label>
              <label className="flex items-center gap-2 text-gray-400">
                <input
                  type="checkbox"
                  checked={formData.hospitalization}
                  onChange={(e) => setFormData({ ...formData, hospitalization: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Hospitalização
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Ações Imediatas</label>
              <textarea
                value={formData.immediate_actions}
                onChange={(e) => setFormData({ ...formData, immediate_actions: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
            {occurrence && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ações Corretivas</label>
                  <textarea
                    value={formData.corrective_actions}
                    onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })}
                    className="input w-full"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Atestado Médico</label>
                  <textarea
                    value={formData.medical_certificate_data}
                    onChange={(e) => setFormData({ ...formData, medical_certificate_data: e.target.value })}
                    className="input w-full"
                    rows={2}
                    placeholder="Médico, CRM, período (ex: Dr. João Silva - CRM 12345 - 10/06 a 15/06/2026)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Lições Aprendidas</label>
                  <textarea
                    value={formData.lessons_learned}
                    onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
                    className="input w-full"
                    rows={2}
                  />
                </div>
              </>
            )}
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const OccurrenceViewModal = ({ occurrence, approving, onApprove, onClose }) => {
  const isApproved = !!occurrence.approved_at;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {occurrence.code}
              <span className={`px-2 py-1 rounded-full text-xs ${TYPE_LABELS[occurrence.occurrence_type]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                {TYPE_LABELS[occurrence.occurrence_type]?.label || occurrence.occurrence_type}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs ${SEVERITY_LABELS[occurrence.severity]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                {SEVERITY_LABELS[occurrence.severity]?.label || occurrence.severity}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[occurrence.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                {STATUS_LABELS[occurrence.status]?.label || occurrence.status}
              </span>
            </h2>
            <p className="text-xs text-gray-500">F-029 - Relatório de Ocorrência - SSMA</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {isApproved ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <CheckCircle2 className="text-green-400 shrink-0" size={22} />
              <p className="text-sm text-green-300">
                Aprovado por <span className="font-semibold">{occurrence.approved_by}</span> em {formatDateBR(occurrence.approved_at)}.
              </p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-sm text-yellow-300">Esta ocorrência ainda não foi aprovada.</p>
              <button
                type="button"
                onClick={onApprove}
                disabled={approving}
                className="btn-primary flex items-center gap-2 shrink-0"
              >
                {approving ? <><Loader2 size={16} className="animate-spin" /> Aprovando...</> : <><CheckCircle2 size={16} /> Aprovar</>}
              </button>
            </div>
          )}

          <FormSection title="Identificação da Ocorrência">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ViewRow label="Data do Evento" value={formatDateBR(occurrence.occurrence_date)} />
              <ViewRow label="Horário do Evento" value={occurrence.occurrence_time} />
              <ViewRow label="Data do Relatório" value={formatDateBR(occurrence.report_date)} />
              <ViewRow label="Local da Ocorrência" value={occurrence.location} />
              <ViewRow label="Área" value={occurrence.area} />
              <ViewRow label="Número da ROS" value={occurrence.ros_number} />
              <ViewRow label="Número da CAT" value={occurrence.cat_number} />
              <ViewRow label="Repórter" value={occurrence.reporter_name} />
              <ViewRow label="Atividade" value={occurrence.activity} />
              <ViewRow label="Testemunhas" value={occurrence.witnesses} />
              <ViewRow label="Envolvidos" value={[occurrence.involved_place && 'Place', occurrence.involved_third_parties && `Terceiros${occurrence.third_parties_name ? ` (${occurrence.third_parties_name})` : ''}`].filter(Boolean).join(', ')} />
            </div>
            <ViewRow label="Situação Geradora" value={occurrence.generating_situation} />
          </FormSection>

          <FormSection title="Lesionado(s) / Envolvido(s) no Acidente ou Incidente">
            {occurrence.injured_persons?.length ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr><th>Nome</th><th>Matrícula (RE)</th><th>Idade</th><th>Função</th><th>Tempo na Função</th></tr>
                  </thead>
                  <tbody>
                    {occurrence.injured_persons.map((p, idx) => (
                      <tr key={idx}>
                        <td className="text-white">{p.name}</td>
                        <td className="text-gray-400">{p.registration || '-'}</td>
                        <td className="text-gray-400">{p.age ?? '-'}</td>
                        <td className="text-gray-400">{p.function || '-'}</td>
                        <td className="text-gray-400">{p.time_in_function || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nenhum lesionado/envolvido informado</p>
            )}
          </FormSection>

          <FormSection title="Detalhamento em Caso de Acidente">
            <ViewRow label="Categoria do Evento" value={EVENT_CATEGORY_LABELS[occurrence.event_category]} />
            {occurrence.event_category === 'injury_illness' && (
              <>
                <ViewRow label="Subtipo" value={INJURY_ILLNESS_SUBTYPE_LABELS[occurrence.injury_illness_subtype]} />
                <ViewRow label="Natureza da Lesão ou Doença" value={occurrence.injury_nature} />
                <ViewRow label="Objeto que Causou a Lesão" value={occurrence.injury_causing_object} />
              </>
            )}
            {occurrence.event_category === 'property_damage' && (
              <>
                <ViewRow label="Subtipo" value={PROPERTY_DAMAGE_SUBTYPE_LABELS[occurrence.property_damage_subtype]} />
                <ViewRow label="Tipo" value={occurrence.property_damage_type} />
                <ViewRow label="Objeto que Causou o Dano" value={occurrence.property_damage_object} />
              </>
            )}
            {occurrence.event_category === 'environmental_damage' && (
              <>
                <ViewRow label="Subtipo" value={ENVIRONMENTAL_DAMAGE_SUBTYPE_LABELS[occurrence.environmental_damage_subtype]} />
                <ViewRow label="Natureza da Perda" value={occurrence.environmental_damage_nature} />
                <ViewRow label="Objeto Relacionado" value={occurrence.environmental_damage_object} />
              </>
            )}
            {occurrence.event_category === 'incident' && (
              <>
                <ViewRow label="Subtipo" value={INCIDENT_SUBTYPE_LABELS[occurrence.incident_subtype]} />
                <ViewRow label="Objeto Relacionado" value={occurrence.incident_object} />
              </>
            )}
          </FormSection>

          <FormSection title="Descrição do Evento">
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{occurrence.description || '-'}</p>
          </FormSection>

          <FormSection title="Potencial de Gravidade">
            <ViewRow label="Potencial de Gravidade" value={SEVERITY_POTENTIAL_LABELS[occurrence.severity_potential]?.label} />
          </FormSection>

          <FormSection title="Causas Apuradas">
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{occurrence.root_cause || '-'}</p>
          </FormSection>

          <FormSection title="Ações Preventivas">
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{occurrence.preventive_actions || '-'}</p>
          </FormSection>

          <FormSection title="Evidências Fotográficas">
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{occurrence.evidence_description || '-'}</p>
          </FormSection>

          <FormSection title="Consequências e Acompanhamento">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <ViewRow label="Tipo de Lesão" value={occurrence.injury_type} />
              <ViewRow label="Parte do Corpo" value={occurrence.body_part} />
              <ViewRow label="Dias Perdidos" value={occurrence.days_lost} />
              <ViewRow label="Atendimento Médico" value={occurrence.medical_treatment ? 'Sim' : 'Não'} />
              <ViewRow label="Hospitalização" value={occurrence.hospitalization ? 'Sim' : 'Não'} />
            </div>
            <ViewRow label="Ações Imediatas" value={occurrence.immediate_actions} />
            <ViewRow label="Ações Corretivas" value={occurrence.corrective_actions} />
            <ViewRow label="Atestado Médico" value={occurrence.medical_certificate_data} />
            <ViewRow label="Lições Aprendidas" value={occurrence.lessons_learned} />
            <ViewRow label="Data de Encerramento" value={formatDateBR(occurrence.closure_date)} />
          </FormSection>
        </div>

        <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-700">
          <button type="button" onClick={onClose} className="btn-secondary">Fechar</button>
        </div>
      </div>
    </>
  );
};

const EPIModal = ({ epi, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: epi?.name || '',
    description: epi?.description || '',
    epi_type: epi?.epi_type || '',
    ca_number: epi?.ca_number || '',
    manufacturer: epi?.manufacturer || '',
    supplier: epi?.supplier || '',
    minimum_stock: epi?.minimum_stock || 0,
    current_stock: epi?.current_stock || 0,
    unit: epi?.unit || 'un',
    shelf_life: epi?.shelf_life || '',
    expiry_date: epi?.expiry_date || '',
    required_areas: epi?.required_areas || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = {
      ...formData,
      shelf_life: formData.shelf_life ? parseInt(formData.shelf_life) : null,
      expiry_date: formData.expiry_date || null,
    };
    await onSave(data);
    setSaving(false);
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {epi ? 'Editar EPI' : 'Novo EPI'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
              <input
                type="text"
                value={formData.epi_type}
                onChange={(e) => setFormData({ ...formData, epi_type: e.target.value })}
                className="input w-full"
                placeholder="Ex: Capacete, Luvas, Óculos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nº CA</label>
              <input
                type="text"
                value={formData.ca_number}
                onChange={(e) => setFormData({ ...formData, ca_number: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fabricante</label>
              <input
                type="text"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fornecedor</label>
              <input
                type="text"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Estoque Atual</label>
              <input
                type="number"
                min="0"
                value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: parseInt(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Estoque Mínimo</label>
              <input
                type="number"
                min="0"
                value={formData.minimum_stock}
                onChange={(e) => setFormData({ ...formData, minimum_stock: parseInt(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Unidade</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Vida Útil (dias)</label>
              <input
                type="number"
                min="0"
                value={formData.shelf_life}
                onChange={(e) => setFormData({ ...formData, shelf_life: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data de Validade</label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Áreas de Uso</label>
              <input
                type="text"
                value={formData.required_areas}
                onChange={(e) => setFormData({ ...formData, required_areas: e.target.value })}
                className="input w-full"
                placeholder="Ex: Produção, Manutenção, Laboratório"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const DELIVERY_REASON_LABELS = {
  new: 'Novo (primeira entrega)',
  replacement: 'Reposição (troca periódica)',
  damage: 'Substituição por dano',
};

const DeliveryModal = ({ epi, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    quantity: 1,
    employee_name: '',
    employee_id: '',
    department: '',
    reason: 'new',
    expected_return_date: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_name.trim()) {
      toast.error('Informe o nome do funcionário');
      return;
    }
    if (!formData.quantity || formData.quantity < 1) {
      toast.error('Informe uma quantidade válida');
      return;
    }
    if (formData.quantity > epi.current_stock) {
      toast.error(`Estoque insuficiente. Disponível: ${epi.current_stock}`);
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...formData,
        quantity: parseInt(formData.quantity),
        expected_return_date: formData.expected_return_date || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Registrar Entrega de EPI</h2>
            <p className="text-gray-400 text-sm">{epi.code} - {epi.name} (estoque: {epi.current_stock})</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Funcionário *</label>
              <input
                type="text"
                value={formData.employee_name}
                onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Matrícula</label>
              <input
                type="text"
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Departamento</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Quantidade *</label>
              <input
                type="number"
                min="1"
                max={epi.current_stock}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Motivo</label>
              <select
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="input w-full"
              >
                {Object.entries(DELIVERY_REASON_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Data Prevista de Devolução</label>
              <input
                type="date"
                value={formData.expected_return_date}
                onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Registrar Entrega'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const DeliveriesHistoryModal = ({ epi, deliveries, onClose }) => (
  <>
    <div className="modal-backdrop" />
    <div className="modal-content p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Histórico de Entregas</h2>
          <p className="text-gray-400 text-sm">{epi.code} - {epi.name}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={24} />
        </button>
      </div>
      {deliveries.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Nenhuma entrega registrada para este EPI</p>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Funcionário</th>
                <th>Depto.</th>
                <th className="text-right">Qtd.</th>
                <th>Motivo</th>
                <th>Devolução Prevista</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id}>
                  <td className="text-gray-400">{formatDateBR(d.delivery_date)}</td>
                  <td className="text-white">{d.employee_name}</td>
                  <td className="text-gray-400">{d.department || '-'}</td>
                  <td className="text-right text-white">{d.quantity}</td>
                  <td className="text-gray-400">{DELIVERY_REASON_LABELS[d.reason] || d.reason || '-'}</td>
                  <td className="text-gray-400">{d.expected_return_date ? formatDateBR(d.expected_return_date) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end pt-4 mt-4 border-t border-gray-700">
        <button onClick={onClose} className="btn-secondary">Fechar</button>
      </div>
    </div>
  </>
);

export default SST;
